import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { platform_school_api } from "../../DAL/Platform/Console";
import { C } from "./consoleTheme";
import { BackBar, Panel, Field, Grid, Stat, Flag, StateScreen, AuditList } from "./ConsoleShared";

/**
 * One organisation, as the platform sees it.
 *
 * Everything here is something the school cannot see about itself, or cannot
 * see in this shape: commercial terms, seat economics, whether its instructors
 * can actually be paid, how much of its roster has gone quiet. Rebuilding
 * their dashboard behind a staff login would be impersonation with extra
 * steps — it answers "what do they see", which is rarely the question.
 */
export default function ConsoleSchool() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await platform_school_api(id);
        if (dead) return;
        if (res?.code === 200) setD(res); else setErr(res?.message || "Could not load");
      } catch (e) { if (!dead) setErr(e?.message || "Could not load"); }
    })();
    return () => { dead = true; };
  }, [id]);

  if (err) return <StateScreen><BackBar label="Console" /><p style={{ color: C.high }}>{err}</p></StateScreen>;
  if (!d) return <StateScreen><BackBar label="Console" />Loading…</StateScreen>;

  const { school, contract, roster, instructors, sessions, flags, staffHistory, modules, tutorsConfig } = d;
  const money = (n) => (n == null ? null : `${contract.currency === "USD" ? "$" : "£"}${n}`);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: 28, color: C.text }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <BackBar label="Console" />

        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{school.name}</h1>
          <div style={{ color: C.textDim, fontSize: 13, marginTop: 4 }}>
            {school.status} · {school.effectiveRegion} ({school.authority})
            {!school.region && <span style={{ color: C.medium }}> · region not set</span>}
          </div>
        </div>

        {/* Risks first. A staff member opening a school is usually asking
            "is anything wrong here", and making them read four panels to
            find out is how problems get missed. */}
        {flags.length > 0 && (
          <Panel title="Needs attention">
            {flags.map((f, i) => <Flag key={i} level={f.level} text={f.text} />)}
          </Panel>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 }}>
          <Stat label="Active students" value={roster.active} />
          <Stat label="Seat utilisation" value={contract.seatUtilisation == null ? "—" : `${contract.seatUtilisation}%`} />
          <Stat label="Bookable instructors" value={instructors.bookable} />
          <Stat
            label="Cannot be paid"
            value={instructors.bookableWithoutPayoutAccount}
            tone={instructors.bookableWithoutPayoutAccount ? C.high : C.good}
          />
        </div>

        <Panel title="Contract">
          <Grid>
            <Field label="Included seats" value={contract.includedSeats} />
            <Field label="Per student / month" value={money(contract.monthlyStudentFee)} />
            <Field label="Currency" value={contract.currency} />
            <Field label="Started" value={contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : null} />
            <Field label="Over seat allowance" value={contract.overSeat || "0"} />
            <Field label="Stripe customer" value={contract.stripeCustomerId} mono />
          </Grid>
        </Panel>

        <Panel title="Roster">
          <Grid min={140}>
            <Field label="Students" value={roster.students} />
            <Field label="Active" value={roster.active} />
            <Field label="Inactive" value={roster.inactive} />
            <Field label={`Dormant (${roster.dormantThresholdDays}d)`} value={roster.dormant} />
          </Grid>
        </Panel>

        <Panel title={`Instructors (${instructors.total})`}>
          {instructors.list.map((a, i) => (
            <div key={a.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 0", borderTop: i ? `1px solid ${C.border}` : "none", fontSize: 13,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>{a.name || a.email}</div>
                <div style={{ color: C.textFaint, fontSize: 12 }}>{a.name ? a.email : ""} · {a.role}</div>
              </div>
              {a.bookable && (
                <span style={{
                  fontSize: 11, borderRadius: 4, padding: "1px 6px",
                  color: a.payoutReady ? C.good : C.high,
                  border: `1px solid ${a.payoutReady ? C.good : C.high}`,
                }}>
                  {a.payoutReady ? "bookable" : "bookable · no payout account"}
                </span>
              )}
              {a.active === false && <span style={{ fontSize: 11, color: C.textFaint }}>inactive</span>}
            </div>
          ))}
        </Panel>

        <Panel title="Sessions">
          <Grid min={130}>
            <Field label="Total" value={sessions.total} />
            <Field label="Pending" value={sessions.pending} />
            <Field label="Confirmed" value={sessions.confirmed} />
            <Field label="Cancelled" value={sessions.cancelled} />
            <Field label="Paid" value={sessions.paid} />
            <Field label="Awaiting payout" value={sessions.awaitingPayout} />
          </Grid>
        </Panel>

        <Panel title="Configuration">
          <Grid>
            <Field label="Modules" value={Object.entries(modules || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "none"} />
            <Field label="Open marketplace" value={tutorsConfig?.allowOpenMarketplace ? "yes" : "no"} />
            <Field label="Payout mode" value={tutorsConfig?.payoutMode} />
          </Grid>
        </Panel>

        <Panel
          title="What XAir staff have done to this school"
          right={
            /* Impersonation is still reachable, but from here it reads as the
               escalation it is rather than the front door. */
            <button
              onClick={() => navigate(`/flight-schools/${school.id}`)}
              style={{
                background: "none", border: `1px solid ${C.border}`, color: C.textDim,
                borderRadius: 5, padding: "3px 9px", fontSize: 12, cursor: "pointer",
              }}
            >
              Open school record
            </button>
          }
        >
          <AuditList rows={staffHistory} />
        </Panel>
      </div>
    </div>
  );
}
